class UnifiedVideoObserver {
  constructor( selector ) {
    this.selector = selector;
    this.players = [];
    this.intersectionStates = new Map();
  }

  init() {
    this.loadYouTubeAPI()
      .then( () => this.setup() )
      .catch( () => {
        this.setup();
      } );
  }

  loadYouTubeAPI() {
    return new Promise( ( resolve, reject ) => {
      if ( window.YT?.Player ) {
        resolve();
        return;
      }

      if ( window._ytApiLoading ) {
        const checkInterval = setInterval( () => {
          if ( window.YT?.Player ) {
            clearInterval( checkInterval );
            resolve();
          }
        }, 100 );
        setTimeout( () => {
          clearInterval( checkInterval );
          if ( !window.YT?.Player ) {
            reject( new Error( 'YouTube API failed to load' ) );
          }
        }, 10000 );
        return;
      }

      window._ytApiLoading = true;
      const tag = document.createElement( 'script' );
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.onerror = () => {
        window._ytApiLoading = false;
        reject( new Error( 'Failed to load YouTube API script' ) );
      };
      document.head.appendChild( tag );

      window.onYouTubeIframeAPIReady = () => {
        window._ytApiLoading = false;
        resolve();
      };

      setTimeout( () => {
        if ( !window.YT?.Player ) {
          window._ytApiLoading = false;
          reject( new Error( 'YouTube API failed to load within timeout' ) );
        }
      }, 10000 );
    } );
  }

  getRootMargin() {
    const header = document.querySelector( 'header' );
    const headerHeight = header ? Math.max( header.getBoundingClientRect().height, 0 ) : 0;
    return `-${headerHeight}px 0px 0px 0px`;
  }

  setup() {
    const elements = document.querySelectorAll( this.selector );
    const rootMargin = this.getRootMargin();

    elements.forEach( ( element ) => {
      const iframe = element.querySelector( 'iframe' );
      const video = element.querySelector( 'video' );

      if ( iframe && window.YT?.Player ) {
        const ytPlayer = new YT.Player( iframe, { // eslint-disable-line no-undef
          events: {
            onReady: ( event ) => {
              const obj = { type: 'youtube', player: event.target, element };
              this.players.push( obj );
              this.observe( element, obj, rootMargin );
              setTimeout( () => this.checkAndPlayFullyVisibleVideo(), 200 );
            },
          },
        } );
        element.ytPlayer = ytPlayer;
      } else if ( video ) {
        const obj = { type: 'html5', player: video, element };
        this.players.push( obj );
        this.observe( element, obj, rootMargin );
        setTimeout( () => this.checkAndPlayFullyVisibleVideo(), 200 );
      }
    } );
  }

  observe( element, playerObj, rootMargin ) {
    const observer = new IntersectionObserver(
      ( entries ) => {
        entries.forEach( ( entry ) => {
          const currentRatio = entry.intersectionRatio;
          const isFullyVisible = entry.isIntersecting && currentRatio === 1.0;
          
          this.intersectionStates.set( playerObj, currentRatio );
          
          if ( isFullyVisible ) {
            this.play( playerObj );
          } else {
            this.pause( playerObj );
          }
        } );
      },
      {
        root: null,
        rootMargin: rootMargin,
        threshold: [ 0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 0.95, 0.98, 0.99, 1.0 ],
      }
    );
    observer.observe( element );
  }

  checkAndPlayFullyVisibleVideo() {
    this.players.forEach( ( playerObj ) => {
      const intersectionRatio = this.intersectionStates.get( playerObj ) || 0;
      
      if ( intersectionRatio === 1.0 ) {
        let isPaused = false;
        try {
          if ( playerObj.type === 'youtube' ) {
            const state = playerObj.player.getPlayerState ? playerObj.player.getPlayerState() : -1;
            isPaused = state === 2 || state === -1;
          } else {
            isPaused = playerObj.player.paused;
          }
        } catch {
          isPaused = true;
        }
        
        if ( isPaused ) {
          this.play( playerObj );
        }
      } else {
        this.pause( playerObj );
      }
    } );
  }

  play( obj ) {
    try {
      if ( obj.type === 'youtube' ) {
        obj.player.playVideo();
      } else {
        obj.player.play().catch( () => {} );
      }
    } catch {
      // Silently handle errors
    }
  }

  pause( obj ) {
    try {
      if ( obj.type === 'youtube' ) {
        obj.player.pauseVideo();
      } else {
        obj.player.pause();
      }
    } catch {
      // Silently handle errors
    }
  }
}

// Initialize
const unifiedObserver = new UnifiedVideoObserver( '.e-div--vid--pause-on-exit' );
unifiedObserver.init();
